/**
 * Certified Bank Account payment flow using Dynamo Stream Machine.
 */
import Big from "big.js";
import React, { createRef, useEffect, useState } from "react";
import { StyleSheet, View, Linking, Platform } from "react-native";
import * as ExpoLinking from "expo-linking";

import { WebView } from "react-native-webview";
import { WebView as YodleeWidgetWeb } from "react-native-web-webview";
import { useTheme } from "react-native-paper";
import { useNavigation } from "@react-navigation/native";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/reducers";
import { HomeParamsList } from "../../navigation/home-stack/home-stack";
import { InfoBoxIconType, InfoBoxType, PaymentSourceType } from "../../enums";
import { translate } from "../../i18n";
import { parseAccountDataFromYodleeCallbackUrl } from "../../utils/yodlee";
import { ThemeType } from "../../theme/theme";
import { useConfig } from "../../hooks/useConfig";
import { Stack } from "../../components/layout/stack";
import { Button, ErrorMessage, InfoBox, Spacer, Text, TextInput } from "../../components";
import { testProps } from "../../utils/testProps";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { bankAccountNumberConfirmationSchema } from "./utils/validation-schemas-payment";
import {
  BankAccountNumberConfirmationFormFields,
  StateToActionMapping,
} from "../../enums/certified-ba";
import {
  DynamoStreamMachineService,
  STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_COMMANDS,
  STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_MACHINE_ID,
  STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES,
} from "../../api/DynamoStreamMachineService";
import { DomusoApiService } from "../../api/DomusoApiService";
import * as storage from "../../utils/storage";
import { FullScreenLoader } from "../../components/new/loader/full-screen-loader";

const { MANUAL_BANK_ACCOUNT_NUMBER, MANUAL_RE_BANK_ACCOUNT_NUMBER } =
  BankAccountNumberConfirmationFormFields;
const SCREENS = Object.freeze({
  // Payments
  PaymentFlowPaymentMethodCertifiedACHYodleeWidgetReviewPayment:
    "PaymentFlowPaymentMethodCertifiedACHYodleeWidgetReviewPayment",
  PaymentFlowPaymentMethodCertifiedACH: "PaymentFlowPaymentMethodCertifiedACH",
  PaymentFlowPaymentTrasmitted: "PaymentFlowPaymentTrasmitted",
});

const YODLEEWIDGET_PERSISTANCE_KEY = "YODLEEWIDGET_PERSISTANCE_KEY";
const MAX_ACCOUNT_NUMBER_CHAR_LENGTH = 17;

export const PaymentFlowPaymentMethodCertifiedACHYodleeWidget = ({ route }: any) => {
  const logger = useSelector((state: RootState) => state.logger);
  const theme = useTheme() as ThemeType;

  let { params = {} as HomeParamsList } = route;
  const { colors, spacing, fontSize } = theme;
  const navigation = useNavigation();
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
  const [uri, setUri] = useState("");
  const [postData, setPostData] = useState("");
  const [yodleeVersion, setYodleeVersion] = useState(null);
  const dispatch = useDispatch();
  const config = useConfig();

  let webview: any = null;
  const cognitoUser = useSelector((state: RootState) => state.auth.cognitoUser);
  // Params passed to the component from navigation.
  const { reservationId, amounts, currentProperty } = params;
  const propertyFees = useSelector((state: RootState) => state.reservation.propertyFees);
  const platformFee = Big(propertyFees?.domusoACH?.fee ? propertyFees?.domusoACH?.fee : 0);
  const dynamoStreamMachineService = new DynamoStreamMachineService(cognitoUser, dispatch);
  const domusoApiService = new DomusoApiService(cognitoUser, dispatch);

  // To keep track of the created state machine when calling DyanmoStreamMachineService
  const [stateMachine, setStateMachine] = useState<any>(null);
  // Keeping track of dynamo stream machine entity ID.
  const [currentStateMachineEntityID, setCurrentStateMachineEntityID] = useState<string>(null);
  // Used to control the polling flow.
  const [needToPoll, setNeedToPoll] = useState(false);
  // Used for tracking the current states that we are trying to poll from stream machine.
  const [stateMachineStatesToPoll, setStateMachineStatesToPoll] = useState<string[]>([]);
  // Used to show the modal for user to confirm account number.
  const [showAnConfirmationScreen, setShowAnConfirmationScreen] = useState(false);
  const [handleYodleeWebResponse, setHandleYodleeWebResponse] = useState<boolean>(false);
  const [apiError, setApiError] = useState<boolean>(false);
  const accountNumberInputRef = createRef();
  const reAccountNumberInputRef = createRef();
  const defaultValues = {
    manualBankAccountNumber: null,
    manualReBankAccountNumber: null,
  };

  const {
    register,
    trigger,
    handleSubmit,
    setValue,
    getValues,
    clearErrors,
    formState: { errors },
  } = useForm<any>({
    resolver: yupResolver(bankAccountNumberConfirmationSchema),
    defaultValues,
  });

  const handleFieldUpdate = async (field: BankAccountNumberConfirmationFormFields, val: string) => {
    setValue(field, val);
  };

  // This Effect is used only for web and is intended to set the state machine using the values from storage.
  // This is because Yodlee widget in web is loaded in a iframe and after the callback redirect the state is lost.
  useEffect(() => {
    (async () => {
      //isYodleeRedirect will only be set during the redirect executed from the Yodlee widget when
      //user closes or selects an account for payment
      if (Platform.OS === "web" && params.isYodleeRedirect) {
        const stored = await storage.load(YODLEEWIDGET_PERSISTANCE_KEY);
        if (stored?.yodleeState) {
          setStateMachine(stored.yodleeState?.params.stateMachineFromPrevScreen);
          setCurrentStateMachineEntityID(stored.yodleeState?.params.stateMachineEntityID);
          if (!stored.yodleeState.responseHandled) {
            setHandleYodleeWebResponse(true); //Used to determine if the component was loaded after the yodlee widget redirect
          } else {
            // we already handled the web response, but its likely the instance of the app
            // that did that is going to die. lets start polling for responses from this instance
            setStateMachine(stored.yodleeState?.params.stateMachineFromPrevScreen);
            setCurrentStateMachineEntityID(stored.yodleeState?.params.stateMachineEntityID);
            setNeedToPoll(true);
            setStateMachineStatesToPoll([
              STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.LINK_FAILED,
              STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.PMT_SRC_FAILED,
              STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.AN_CONFIRMATION,
              STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.INVALID_RN,
              STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.BLACKLISTED_RN,
              STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.PREDICTOR_APPROVED,
              STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.PREDICTOR_DENIED,
              STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.PREDICTOR_FAILED,
            ]);
          }
        }
      }
    })();
  }, []);

  // This Effect used only for web and will only be executed when handleYodleeWebResponse is set.
  // Is intended to check the yodlee reponse to determine if the user canceled or closed the widget or if selectec a bank account
  useEffect(() => {
    (async () => {
      if (Platform.OS === "web" && handleYodleeWebResponse) {
        const stored: any = await storage.load(YODLEEWIDGET_PERSISTANCE_KEY);
        if (stored.yodleeState.responseHandled) {
          return;
        } else {
          // store the fact that we are handling this. if we don't do this, the other instance that is
          // racing this one will also try to handle this response and cause duplicate state transitions
          // to be sent which crashes the app
          stored.yodleeState.responseHandled = true;
          await storage.save(YODLEEWIDGET_PERSISTANCE_KEY, stored);
        }
        // Yodlee widget will not set JSONcallBackStatus when the user cancels or closed the widget.
        if (route?.params?.JSONcallBackStatus) {
          try {
            const jsonCallback = JSON.parse(route.params.JSONcallBackStatus);
            setShowLoadingSpinner(true);
            // Yodlee widget will only return VERIFICATION_SUCCESS if the user selected a bank account.
            if (jsonCallback.status === "VERIFICATION_SUCCESS") {
              const itemAccountId = jsonCallback.itemAccountId;
              await startPaymentLink(`${itemAccountId}`);
            } else if (jsonCallback.status === "USER_ABORTED_VERIFICATION") {
              //Means user closed widget while validating credentials
              onCloseButtonPressed();
            }
            setHandleYodleeWebResponse(false); //Used to determine if the component was loaded after the yodlee widget redirect
          } catch (e) {
            logger.log(`Error handling web response ${e}`);
          }
        }
      }
    })();
  }, [handleYodleeWebResponse]);

  useEffect(() => {
    let accountNumber: BankAccountNumberConfirmationFormFields = MANUAL_BANK_ACCOUNT_NUMBER;
    let reAccountNumber: BankAccountNumberConfirmationFormFields = MANUAL_RE_BANK_ACCOUNT_NUMBER;
    register(accountNumber);
    register(reAccountNumber);
  }, [register]);

  // This useEffect only called once after the first render.
  useEffect(() => {
    (async () => {
      if (!params.isYodleeRedirect) {
        logger.info(`Initial useEffect called to create state machine to process payment`);
        try {
          const result = await domusoApiService.getYodleeVersion();
          setYodleeVersion(result.body.version);
          const createdStateMachine = await createPaymentStateMachine(result.body.version);
          setCurrentStateMachineEntityID(createdStateMachine.item.SK);
          setStateMachine(createdStateMachine);
          setStateMachineStatesToPoll([
            STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.INIT_FAILED,
            STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.INIT_RESPONDED,
            STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.PMT_SRC_FAILED,
            STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.AN_CONFIRMATION,
            STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.INVALID_RN,
            STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.BLACKLISTED_RN,
            STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.PREDICTOR_APPROVED,
            STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.PREDICTOR_DENIED,
            STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.PREDICTOR_FAILED,
          ]);
          setNeedToPoll(true);
        } catch (e) {
          logger.error(e);
          setApiError(true);
        }
      }
    })();
  }, []);

  // This effect is used for polling the DynamoStreamMachine.
  useEffect(() => {
    (async () => {
      if (!needToPoll || stateMachineStatesToPoll.length == 0) {
        return;
      }
      // For web it will execute before the callback redirect or if is already from the redirect only when
      // handleYodleeWebResponse is set which means statemachine was already loaded from storage.
      // This is because Yodlee widget in web is loaded in a iframe and after the callback redirect the state is lost.
      if (params.isYodleeRedirect && !stateMachine) {
        logger.log("No state machine created skipping cannot do the state machine polling");
        return;
      }
      logger.log(
        `useEffect state machine state polling called --> currentStateMachineEntityID: ${currentStateMachineEntityID}`
      );
      // If need to poll and there states to look for
      logger.log(
        `Should be polling: ${needToPoll} streamMachineStatesToPoll: ${stateMachineStatesToPoll}`
      );
      dynamoStreamMachineService.pollMachineState(
        stateMachine?.machineId,
        currentStateMachineEntityID,
        stateMachineStatesToPoll,
        30,
        setStateMachine
      );
    })();
  }, [needToPoll, stateMachineStatesToPoll]);

  // Effect is used to take an action based on updated StateMachine which is updated from Polling function.
  useEffect(() => {
    logger.log(
      `useEffect action trigger called --> currentStateMachineEntityID: ${currentStateMachineEntityID}`
    );
    if (!stateMachine) {
      // haven't loaded the machine from storage yet
      return;
    }
    if (
      params.isYodleeRedirect &&
      stateMachine.item.machine_state === STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.INIT_RESPONDED
    ) {
      // if this is a redirect, and we haven't updated the machine to the next state, dont try to do it again
      return;
    }

    const stateMachineStateToActionMapping: StateToActionMapping = {
      [STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.INIT_FAILED]: onInitFailedState,
      [STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.INIT_RESPONDED]: onInitRespondedState,
      [STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.AN_CONFIRMATION]: onAnConfirmationState,
      [STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.PREDICTOR_APPROVED]: onPredictorApprovedState,
      [STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.PREDICTOR_DENIED]: onPaymentFailure,
      [STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.PREDICTOR_FAILED]: onPaymentFailure,
      [STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.LINK_FAILED]: onFailureStates,
      [STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.PMT_SRC_FAILED]: onFailureStates,
      [STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.INVALID_RN]: onFailureStates,
      [STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.BLACKLISTED_RN]: onFailureStates,
    };
    if (Object.keys(stateMachineStateToActionMapping).includes(stateMachine?.item?.machine_state)) {
      const action = stateMachineStateToActionMapping[stateMachine?.item?.machine_state];
      logger.log(
        `State machine ${stateMachine?.item?.machine_state} State polled,
          Action: ${action?.name}`
      );
      action();
    } else {
      logger.log(
        `No action defined for the state and will do the default failure action--> ${stateMachine?.item?.machine_state}`
      );
    }
    logger.log("No need to poll anymore");
    setNeedToPoll(false);
    if (Platform.OS === "web") {
      if (!params.isYodleeRedirect) {
        const setYodleeState = async () => {
          // Every time we refresh the stateMachine before redirect we need to also update the storage.
          // params are accessible since this is before yodlee redirect.
          params.stateMachineFromPrevScreen = stateMachine;
          params.stateMachineEntityID = currentStateMachineEntityID;
          await storage.save(YODLEEWIDGET_PERSISTANCE_KEY, { yodleeState: { params } });
        };
        //This is the method that creates the yodleState object for first time in storage and then it keeps on updating it.
        setYodleeState();
      }
    }
  }, [stateMachine]);

  /**
   * Util functions.
   */
  const createPaymentStateMachine = async (version: Number) => {
    // Get user information.
    const userDetails = cognitoUser.signInUserSession.idToken.payload;
    const attrs = {
      mysql_user_id: userDetails["custom:domusoId"],
      mysql_user_first_name: userDetails.given_name,
      mysql_user_last_name: userDetails.family_name,
      mysql_reservation_id: reservationId,
      mysql_property_id: currentProperty.id,
      payment_amount: amounts.amount,
      payment_fee: amounts.platformFee,
      payment_type: "DOMUSO_CASH",
      yodlee_version: version,
    };
    const createdStateMachine = await dynamoStreamMachineService.createMachine(
      STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_MACHINE_ID,
      STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_COMMANDS.START_CERTIFIED_BA,
      attrs
    );
    return createdStateMachine;
  };

  async function onCloseButtonPressed() {
    logger.log(`Close button on fastlink widget was clicked`);
    // close button on fastlink widget was clicked
    const attrs = {
      yodlee_account_id: null,
    };

    try {
      let updatedStateMachine;
      if (Platform.OS === "web") {
        const { yodleeState } = await storage.load(YODLEEWIDGET_PERSISTANCE_KEY);
        updatedStateMachine = await dynamoStreamMachineService.transitionMachine(
          yodleeState.params.stateMachineFromPrevScreen?.machineId,
          yodleeState.params.stateMachineEntityID,
          yodleeState.params.stateMachineFromPrevScreen?.item?.machine_state,
          STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_COMMANDS.CLOSE_WIDGET,
          attrs
        );
      } else {
        updatedStateMachine = await dynamoStreamMachineService.transitionMachine(
          stateMachine?.machineId,
          currentStateMachineEntityID,
          stateMachine?.item?.machine_state,
          STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_COMMANDS.CLOSE_WIDGET,
          attrs
        );
      }

      setStateMachine(updatedStateMachine);

      if (Platform.OS === "web") {
        //If we call navigation.navigate here it will load the view PaymentFlowPaymentMethodCertifiedACH inside the iframe.
        //Need to rely on post message so we can call from parent window.
        window.top.postMessage("closedYodleeWidget", `${window.location.origin}`);
      } else {
        navigation.navigate(SCREENS.PaymentFlowPaymentMethodCertifiedACH, { ...params });
      }
    } catch (e) {
      logger.error(e);
      setApiError(true);
    }
  }

  window.onmessage = function (event) {
    try {
      if (event.data === "closedYodleeWidget") {
        const navigateBack = async () => {
          const { yodleeState } = await storage.load(YODLEEWIDGET_PERSISTANCE_KEY);
          navigation.navigate(SCREENS.PaymentFlowPaymentMethodCertifiedACH, {
            ...yodleeState.params,
          });
        };
        navigateBack();
      } else if (event.data === "predictorApprovedYodleWidget") {
        const navigateToReviewPayment = async () => {
          const today = new Date();
          //params are lost since this executes after the yodlee redirect so we need to rely on storage.
          const { yodleeState } = await storage.load(YODLEEWIDGET_PERSISTANCE_KEY);
          navigation.navigate(
            SCREENS.PaymentFlowPaymentMethodCertifiedACHYodleeWidgetReviewPayment,
            {
              ...yodleeState.params,
              paymentSourceId:
                yodleeState.params.stateMachineFromPrevScreen.item.mysql_payment_source_id,
              paymentSourceType: PaymentSourceType.BANK_ACCOUNT,
              paymentScheduleType: "ONE_SHOT",
              isCertifiedBA: true,
              paymentDate: today,
              authorizationDate: today,
            }
          );
        };
        navigateToReviewPayment();
      } else if (event.data === "failureYodleeWidget") {
        const navigatedToTransmitted = async () => {
          const { yodleeState } = await storage.load(YODLEEWIDGET_PERSISTANCE_KEY);
          // @ts-ignore
          navigation.navigate(SCREENS.PaymentFlowPaymentTrasmitted, {
            ...yodleeState.params,
            createPaymentResult: {
              type: "FAILURE",
              infoBoxType: InfoBoxType.RED,
              headline:
                yodleeState?.errorState?.headline ??
                translate("paymentFlow.paymentMethodCertifiedBank.errors.paymentUnsuccessful"),
              messageBody:
                yodleeState?.errorState?.messageBody ??
                translate("paymentFlow.paymentMethodCertifiedBank.errors.genericPaymentError"),
            },
          });
        };
        navigatedToTransmitted();
      }
    } catch (e) {
      logger.log(`Error: ${e}`);
      console.error(e);
    }
  };

  // This method will be called when user successfully selects an account from yodlee widget.
  async function startPaymentLink(yodleeAccountId: string) {
    logger.log(`Started payment link process`);

    const attrs = {
      yodlee_account_id: yodleeAccountId,
    };

    try {
      let updatedStateMachine;
      if (Platform.OS === "web") {
        const stored = await storage.load(YODLEEWIDGET_PERSISTANCE_KEY);
        const { yodleeState } = stored;
        updatedStateMachine = await dynamoStreamMachineService.transitionMachine(
          yodleeState.params.stateMachineFromPrevScreen?.machineId,
          yodleeState.params.stateMachineEntityID,
          yodleeState.params.stateMachineFromPrevScreen?.item?.machine_state,
          STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_COMMANDS.STORE_ACCOUNT_ID,
          attrs
        );
        stored.yodleeState.params.stateMachineFromPrevScreen = {
          ...stored.yodleeState.params.stateMachineFromPrevScreen,
          ...updatedStateMachine,
        };
        await storage.save(YODLEEWIDGET_PERSISTANCE_KEY, stored);
      } else {
        updatedStateMachine = await dynamoStreamMachineService.transitionMachine(
          stateMachine?.machineId,
          currentStateMachineEntityID,
          stateMachine?.item?.machine_state,
          STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_COMMANDS.STORE_ACCOUNT_ID,
          attrs
        );
      }

      //This triggers the useEffect that will check what action need to be taken based on last state machine.
      setNeedToPoll(true);
      setStateMachine(updatedStateMachine);
      setStateMachineStatesToPoll([
        STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.LINK_FAILED,
        STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.PMT_SRC_FAILED,
        STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.AN_CONFIRMATION,
        STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.INVALID_RN,
        STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.BLACKLISTED_RN,
        STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.PREDICTOR_APPROVED,
        STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.PREDICTOR_DENIED,
        STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.PREDICTOR_FAILED,
      ]);
    } catch (err) {
      logger.log(`Error happened while update stream machine ${err}`);
      logger.log(JSON.stringify({ err }));
      setApiError(true);
    }
  }

  const onAccountNumberConfirmation = async () => {
    const { manualBankAccountNumber, manualBankReAccountNumber } = getValues();
    logger.log(
      `onAccountNumberConfirmation called,
      account number: ${manualBankAccountNumber}
      account number confirmation: ${manualBankReAccountNumber}
      `
    );
    logger.info(`Form Errors: ${errors}`);

    setShowAnConfirmationScreen(false);

    const attrs = {
      manual_bank_account_number: manualBankAccountNumber,
    };

    try {
      const updatedStateMachine = await dynamoStreamMachineService.transitionMachine(
        stateMachine?.machineId,
        currentStateMachineEntityID,
        stateMachine?.item?.machine_state,
        STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_COMMANDS.USER_CONFIRMS_AN,
        attrs
      );
      setStateMachine(updatedStateMachine);
      setStateMachineStatesToPoll([
        STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.PMT_SRC_FAILED,
        STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.PREDICTOR_APPROVED,
        STATE_MACHINE_CERTIFIED_BANK_ACCOUNT_STATES.PREDICTOR_DENIED,
      ]);
      setNeedToPoll(true);
    } catch (e) {
      logger.error(e);
      setApiError(true);
    }
  };

  const onPaymentFailure = async () => {
    try {
      setShowLoadingSpinner(false);
      let messageBody =
        stateMachine.item.risk_predictor_decline_code === "001"
          ? stateMachine.item.risk_predictor_decline_reason
          : translate("paymentFlow.paymentMethodCertifiedBank.errors.genericPaymentError");

      let headline = translate("paymentFlow.paymentMethodCertifiedBank.errors.paymentUnsuccessful");

      const error =
        stateMachine?.item?.risk_predictor_decline_code ||
        stateMachine?.item?.risk_predictor_decline_reason
          ? `predictorDeclineCode: ${stateMachine?.item?.risk_predictor_decline_code} predictorDeclinedReason: ${stateMachine?.item?.risk_predictor_decline_reason}`
          : "There was no predictor decline code";
      // pull error
      logger.error({
        exception: error,
        reservationId: reservationId,
        paymentScheduleType: "ONE_SHOT",
        message: "Yodlee link account error",
        paymentSourceType: "BANK_ACCOUNT_DOMUSO_CASH",
      });

      if (Platform.OS === "web") {
        const stored = await storage.load(YODLEEWIDGET_PERSISTANCE_KEY);
        stored.yodleeState.errorState = { messageBody, headline };
        logger.log("Adding error message to storage");
        await storage.save(YODLEEWIDGET_PERSISTANCE_KEY, stored);
        window.top.postMessage("failureYodleeWidget", `${window.location.origin}`);
      } else {
        return navigation.navigate(SCREENS.PaymentFlowPaymentTrasmitted, {
          ...params,
          createPaymentResult: {
            type: "FAILURE",
            infoBoxType: InfoBoxType.RED,
            headline,
            messageBody,
          },
        });
      }
    } catch (e) {
      logger.log(`error: ${e}`);
      console.error(e);
    }
  };

  const onFailureStates = async () => {
    setShowLoadingSpinner(false);
    let headline = translate(
      "paymentFlow.paymentMethodCertifiedBank.errors.unableToLinkAccountTitle"
    );
    let messageBody = translate(
      "paymentFlow.paymentMethodCertifiedBank.errors.unableToLinkAccountMessage"
    );

    if (stateMachine?.item?.machine_state == "BLACKLISTED_RN") {
      headline = translate(`errors.YE02.title`);
      messageBody = translate(`errors.YE02.message`);
    } else if (stateMachine?.item?.machine_state == "INVALID_RN") {
      headline = translate(`errors.YE03.title`);
      messageBody = translate(`errors.YE03.message`);
    }
    if (stateMachine.item.reason_code) {
      headline = translate(`errors.${stateMachine.item.reason_code}.title`);
      messageBody = translate(`errors.${stateMachine.item.reason_code}.message`);
    }
    const error =
      stateMachine?.item?.result_code || stateMachine?.item?.reason_code
        ? `resultCode: ${stateMachine?.item?.result_code} reasonCode: ${stateMachine?.item?.reason_code}`
        : "There was no account link error provided";
    // pull error
    logger.error({
      exception: error,
      reservationId: reservationId,
      paymentScheduleType: "ONE_SHOT",
      message: "Yodlee link account error",
      paymentSourceType: "BANK_ACCOUNT_DOMUSO_CASH",
    });

    if (Platform.OS === "web") {
      const stored = await storage.load(YODLEEWIDGET_PERSISTANCE_KEY);
      stored.yodleeState.errorState = { messageBody, headline };
      await storage.save(YODLEEWIDGET_PERSISTANCE_KEY, stored);
      window.top.postMessage("failureYodleeWidget", `${window.location.origin}`);
    } else {
      return navigation.navigate(SCREENS.PaymentFlowPaymentTrasmitted, {
        ...params,
        isUnapprovedDCash: true,
        createPaymentResult: {
          type: "FAILURE",
          infoBoxType: InfoBoxType.RED,
          headline,
          messageBody,
        },
      });
    }
  };

  /**
   * Actions to trigger based on different state machine states.
   */
  const onInitFailedState = onCloseButtonPressed;

  const onPredictorApprovedState = () => {
    if (Platform.OS === "web") {
      const doPost = async () => {
        const stored = await storage.load(YODLEEWIDGET_PERSISTANCE_KEY);
        stored.yodleeState.params.stateMachineFromPrevScreen = stateMachine;
        await storage.save(YODLEEWIDGET_PERSISTANCE_KEY, stored);
        window.top.postMessage("predictorApprovedYodleWidget", `${window.location.origin}`);
      };
      doPost();
    } else {
      const today = new Date();
      return navigation.navigate(
        SCREENS.PaymentFlowPaymentMethodCertifiedACHYodleeWidgetReviewPayment,
        {
          ...params,
          paymentSourceId: stateMachine.item.mysql_payment_source_id,
          paymentSourceType: PaymentSourceType.BANK_ACCOUNT,
          paymentScheduleType: "ONE_SHOT",
          isCertifiedBA: true,
          stateMachineEntityID: currentStateMachineEntityID,
          stateMachineFromPrevScreen: stateMachine,
          paymentDate: today,
          authorizationDate: today,
        }
      );
    }
  };

  const InitResponsedStateHandlers = {
    1.0: () => {
      const {
        yodlee_app_id: appId,
        yodlee_access_token: accessToken,
        yodlee_user_token: userToken,
      } = stateMachine.item;

      let callBackUrl = "";
      if (Platform.OS === "web") {
        //After user selects the Bank account from the yodlee's iframe widget or closes the iframe this is the url it will be redirected
        //Redirecting to current page where response will be handled by lookin at isYodleeRedirect param
        callBackUrl = `${window.location.origin}/Actions/PaymentFlowPaymentMethodCertifiedACHYodleeWidget?isYodleeRedirect=true`;
      } else {
        callBackUrl = "https://fastlink.callback";
      }
      const url = `${config.domusoApiService}/v1/fastlink?app=${appId}&token=${accessToken}&rsession=${userToken}&callback=callback=${callBackUrl}`;
      logger.info(
        `Loading Yodlee fast link URL with the following: ${JSON.stringify(
          { url, appId, accessToken, userToken },
          null,
          2
        )}`
      );
      setUri(url);
    },
    1.1: () => {
      const { yodlee_user_token: accessToken } = stateMachine.item;
      const url = config.fastlinkv4.url;
      let postDataConfig = "";
      if (Platform.OS === "web") {
        postDataConfig = `configName=${config.fastlinkv4.config}`;
      } else {
        const intentUrl = ExpoLinking.createURL("");
        logger.log(`Deep link used to redirect user back to domuso app: ${intentUrl}`);
        postDataConfig = `configName=${config.fastlinkv4.config}&intentUrl=${intentUrl}`;
      }
      logger.info(
        `Loading Yodlee fast link URL with the following: ${JSON.stringify(
          { url, accessToken },
          null,
          2
        )}`
      );
      setUri(url);
      setPostData(
        `accessToken=Bearer ${accessToken}&extraParams=${encodeURIComponent(`${postDataConfig}`)}`
      );
    },
  };

  const onInitRespondedState = InitResponsedStateHandlers[yodleeVersion ?? 1.0];

  const onAnConfirmationState = async () => {
    logger.info("AN_CONFIRMATION state");
    setShowAnConfirmationScreen(true);
    logger.log("setShowAnConfirmationModal called");
  };

  async function yodleeOnMessageFunction(event) {
    const { type, data } = JSON.parse(event.nativeEvent.data);
    if (type == "OPEN_EXTERNAL_URL" || type == "BANK_OAUTH_URL") {
      return await openUrl(data.url);
    }
    if (data?.action !== "exit") {
      return;
    }
    const sites = (data?.sites || []).filter((s) => s.status === "SUCCESS");
    if (sites.length === 0) {
      onCloseButtonPressed();
      return;
    }
    setShowLoadingSpinner(true);
    await startPaymentLink(sites[0].accountId);
  }

  const YodleeWebViews = {
    1.0: () =>
      Platform.OS === "web" ? (
        <YodleeWidgetWeb source={{ uri }} />
      ) : (
        <WebView
          ref={(ref) => (webview = ref)}
          style={{ opacity: 0.99 }}
          source={{ uri }}
          onNavigationStateChange={async (all) => {
            const { url } = all;
            if (url === "https://fastlink.callback/") {
              onCloseButtonPressed();
            }
            if (url.startsWith("https://fastlink.callback")) {
              logger.info(`Yodlee fast link done and received a callback --> ${url}`);
              setShowLoadingSpinner(true);
              webview.stopLoading();
              if (url.includes("VERIFICATION_SUCCESS")) {
                const { itemAccountId } = parseAccountDataFromYodleeCallbackUrl(url);
                logger.log(JSON.stringify({ itemAccountId }));
                await startPaymentLink(`${itemAccountId}`);
              } else if (url.includes("USER_ABORTED_VERIFICATION")) {
                //Means user closed widget while validating credentials
                onCloseButtonPressed();
              }
            }
          }}
          onMessage={async (event) => {
            const { type, data } = JSON.parse(event.nativeEvent.data);
            if (type == "OPEN_EXTERNAL_URL" || type == "BANK_OAUTH_URL") {
              return await openUrl(data.url);
            }
          }}
        />
      ),
    1.1: () =>
      Platform.OS === "web" ? (
        <YodleeWidgetWeb
          source={{ uri }}
          onMessage={async (event) => {
            await yodleeOnMessageFunction(event);
          }}
        />
      ) : (
        <WebView
          ref={(ref) => (webview = ref)}
          style={{ opacity: 0.99 }}
          source={{
            uri,
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
            body: postData,
            method: "POST",
          }}
          javaScriptEnabled={true}
          onMessage={async (event) => {
            await yodleeOnMessageFunction(event);
          }}
        />
      ),
  };

  const styles = StyleSheet.create({
    sectionTitle: {
      fontSize: fontSize.large,
      fontWeight: "bold",
      marginTop: spacing.large,
      marginBottom: spacing.large,
      textAlign: "center",
    },
    outerContainer: {
      display: "flex",
      flex: 1,
      padding: spacing.medium,
      flexDirection: "column",
      backgroundColor: colors.background,
    },
  });

  const ANConfirmationScreen = () => (
    <View style={styles.outerContainer}>
      <Text
        tx="paymentFlow.paymentMethodCertifiedBank.yodleeWidget.certifiedPaymentAccountNumberValidation"
        style={styles.sectionTitle}
      />

      {apiError ? (
        <InfoBox
          width="100%"
          headline={translate("paymentFlow.paymentMethodCertifiedBank.errors.error") as string}
          infoBoxType={InfoBoxType.RED}
          usesXClose={true}
          xCloseHandler={() => setApiError(false)}
          headlineIconType={InfoBoxIconType.RED_X}>
          <Text>{translate("paymentFlow.paymentMethodCertifiedBank.errors.unexpectedError")}</Text>
        </InfoBox>
      ) : null}

      <Stack space="tiny">
        <Text tx="paymentFlow.paymentMethodCertifiedBank.yodleeWidget.accountNumber"></Text>
        <TextInput
          error={errors?.[MANUAL_BANK_ACCOUNT_NUMBER]?.message}
          name={MANUAL_BANK_ACCOUNT_NUMBER}
          disabled={false}
          keyboardType={"number-pad"}
          maxLength={MAX_ACCOUNT_NUMBER_CHAR_LENGTH}
          onChangeText={(val: string) => {
            handleFieldUpdate(MANUAL_BANK_ACCOUNT_NUMBER, val);
          }}
          inputAccessoryViewID={"inputAccessoryViewID3"}
          ref={accountNumberInputRef as any}
          onSubmitEditing={handleSubmit}
        />
        <ErrorMessage errors={errors} formName={MANUAL_BANK_ACCOUNT_NUMBER} />
      </Stack>
      <Stack space="tiny">
        <Text tx="paymentFlow.paymentMethodCertifiedBank.yodleeWidget.reAccountNumber"></Text>
        <TextInput
          name={MANUAL_RE_BANK_ACCOUNT_NUMBER}
          error={errors?.[MANUAL_RE_BANK_ACCOUNT_NUMBER]?.message}
          name={MANUAL_RE_BANK_ACCOUNT_NUMBER}
          disabled={false}
          keyboardType={"number-pad"}
          maxLength={MAX_ACCOUNT_NUMBER_CHAR_LENGTH}
          onChangeText={(val: string) => {
            handleFieldUpdate(MANUAL_RE_BANK_ACCOUNT_NUMBER, val);
          }}
          inputAccessoryViewID={"inputAccessoryViewID4"}
          ref={reAccountNumberInputRef as any}
          onSubmitEditing={handleSubmit}
        />
        <ErrorMessage errors={errors} formName={MANUAL_RE_BANK_ACCOUNT_NUMBER} />
      </Stack>
      <Spacer />
      <View accessible={true}>
        <Button
          tx="paymentFlow.paymentMethodCertifiedBank.yodleeWidget.continue"
          mode="contained"
          onPress={handleSubmit(onAccountNumberConfirmation)}
          disabled={false}
          {...testProps("continue-button")}
        />
      </View>
    </View>
  );

  if (showAnConfirmationScreen) {
    logger.info("Showing ANConfirmationModal modal");
    return <ANConfirmationScreen />;
  }

  if (showLoadingSpinner || !uri) {
    logger.info(`Loading ${route.name}`);
    return <FullScreenLoader loading={true} />;
  }
  const openUrl = async (url: string) => {
    const urlSupport = await Linking.canOpenURL(url);
    if (urlSupport) {
      Linking.openURL(url);
    } else {
      logger.info(`Cannot open urls`);
    }
  };
  logger.info(`DATA: ${uri}\n${postData}`);
  return YodleeWebViews[yodleeVersion ?? 1.0]();
};

export default PaymentFlowPaymentMethodCertifiedACHYodleeWidget;
